import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, X } from "lucide-react";
import QuoteProposal, { accentInk } from "@/components/proposal/QuoteProposal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DECLINE_REASONS = ["Price", "Timing", "Scope", "Going with another contractor", "No longer required", "Other"];

// Public customer proposal — the token in the URL is the credential.
export default function PublicQuote() {
  const { token } = useParams();
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [declineOpen, setDeclineOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [declineComment, setDeclineComment] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/quotes/${token}`)
      .then((r) => setDoc(r.data))
      .catch(() => setErr("This link is invalid or the quote has been removed."));
  }, [token]);

  const respond = async (payload) => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await axios.post(`${API}/public/quotes/${token}/respond`, payload);
      const { data: fresh } = await axios.get(`${API}/public/quotes/${token}`);
      setDoc({ ...fresh, effective_status: data.status === "accepted" ? "accepted" : "declined" });
      setDeclineOpen(false);
      window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
    } catch (e) {
      setError(e.response?.data?.detail || "Something went wrong — please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (err) {
    return (
      <div className="quote-proposal-theme min-h-screen proposal-canvas flex items-center justify-center p-6" data-testid="public-quote-error">
        <div className="proposal-surface border-l-4 border-[#FF5F15] px-8 py-7 max-w-md shadow-lg">
          <div className="text-[11px] font-black uppercase tracking-[0.2em] proposal-text">Quote unavailable</div>
          <p className="mt-2 text-sm proposal-text">{err}</p>
        </div>
      </div>
    );
  }
  if (!doc) {
    return (
      <div className="quote-proposal-theme min-h-screen proposal-canvas flex items-center justify-center" data-testid="public-quote-loading">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF5F15]" />
      </div>
    );
  }

  const accent = doc.accent || "#FF5F15";

  return (
    <div className="quote-proposal-theme" style={{ "--proposal-accent": accent, "--proposal-on-accent": accentInk(accent) }}>
      {error && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 bg-red-600 text-white px-5 py-3 text-sm font-bold shadow-lg" data-testid="public-quote-error-toast">
          {error}
        </div>
      )}
      <QuoteProposal
        doc={doc}
        busy={busy}
        onAccept={({ name, comment, termsOk }) => respond({ action: "accept", name, comment: comment || null, terms_accepted: termsOk })}
        onDecline={() => { setReason(""); setDeclineComment(""); setDeclineOpen(true); }}
      />

      {declineOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-6 print:hidden"
          role="dialog" aria-modal="true" aria-labelledby="decline-title" data-testid="decline-dialog">
          <div className="proposal-surface w-full sm:max-w-lg max-h-[92vh] overflow-y-auto" style={{ borderTop: `5px solid ${accent}` }}>
            <div className="flex items-start justify-between gap-4 px-6 pt-6">
              <div>
                <h2 id="decline-title" className="text-xl font-black uppercase tracking-tight proposal-text">Decline {doc.label?.toLowerCase()}?</h2>
                <p className="text-sm proposal-muted mt-1">We&apos;re sorry this {doc.label?.toLowerCase()} isn&apos;t right for you.</p>
              </div>
              <button onClick={() => setDeclineOpen(false)} aria-label="Close"
                className="proposal-muted " data-testid="decline-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="px-6 py-5">
              <fieldset>
                <legend className="text-[10px] font-black uppercase tracking-[0.18em] proposal-muted mb-2.5">Reason (optional)</legend>
                <div className="space-y-1.5">
                  {DECLINE_REASONS.map((r) => (
                    <label key={r} className="flex items-center gap-2.5 text-sm proposal-text cursor-pointer py-1">
                      <input type="radio" name="decline-reason" value={r} checked={reason === r}
                        onChange={() => setReason(r)} style={{ accentColor: accent }} className="w-4 h-4"
                        data-testid={`decline-reason-${r.toLowerCase().replace(/\s+/g, "-")}`} />
                      {r}
                    </label>
                  ))}
                </div>
              </fieldset>
              <div className="mt-4">
                <label htmlFor="decline-comment" className="block text-[10px] font-black uppercase tracking-[0.18em] proposal-muted mb-1.5">Comment</label>
                <textarea id="decline-comment" rows={3} value={declineComment} onChange={(e) => setDeclineComment(e.target.value)}
                  placeholder="Anything you'd like us to know (optional)…"
                  className="proposal-input w-full border proposal-border px-3 py-2.5 text-sm outline-none "
                  data-testid="decline-comment" />
              </div>
              <div className="mt-3">
                <label htmlFor="decline-name" className="block text-[10px] font-black uppercase tracking-[0.18em] proposal-muted mb-1.5">Your full name *</label>
                <DeclineName accent={accent} onSubmit={(name) => respond({
                  action: "decline", name, reason: reason || null, comment: declineComment.trim() || null,
                })} busy={busy} onCancel={() => setDeclineOpen(false)} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DeclineName({ accent, onSubmit, busy, onCancel }) {
  const [name, setName] = useState("");
  return (
    <>
      <input id="decline-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Jordan Smith" autoComplete="name"
        className="proposal-input w-full border proposal-border px-3 py-2.5 text-sm outline-none "
        data-testid="decline-name" />
      <div className="mt-5 flex flex-col sm:flex-row gap-3">
        <button onClick={onCancel} disabled={busy}
          className="px-6 py-3.5 text-sm font-black uppercase tracking-[0.12em] proposal-text border proposal-border   transition-colors"
          data-testid="decline-cancel">
          Cancel
        </button>
        <button onClick={() => onSubmit(name.trim())} disabled={busy || name.trim().length < 2}
          className="flex-1 px-6 py-3.5 text-sm font-black uppercase tracking-[0.12em] text-white bg-red-600 hover:bg-red-700 transition-colors disabled:opacity-45"
          data-testid="decline-confirm">
          {busy ? "Declining…" : "Decline quote"}
        </button>
      </div>
    </>
  );
}
